import { AfterViewInit, booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, Injector, input, model, NgZone, OnDestroy, output, signal, untracked, viewChild, ViewEncapsulation } from '@angular/core';
import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/annotations';
import 'highcharts/modules/coloraxis';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/heatmap';
import 'highcharts/modules/navigator';
import 'highcharts/modules/stock';
import 'highcharts/modules/treemap';
import { debounced } from 'src/app/pbir/utils/debounced';
import { logger } from 'src/app/pbir/utils/logger';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { debounce } from 'ts-debounce';
import { Size } from '../../../../shared/utils/ResizeListener';
import { AutosizingObserver } from 'src/app/pbir/utils/AutoSizeingObserver';

const LOGGER = logger('PbirHighchartsChartComponent');

Highcharts.setOptions({
  credits: { enabled: false },
  chart: {
    style: {
      fontFamily: '"Lato", sans-serif'
    }
  }
});

@Component({
  selector: 'pbir-highcharts-chart',
  template: `<div #chartContainer class="pbir-highcharts-chart-container"></div>`,
  styleUrl: './PbirHighchartsChart.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PbirHighchartsChartComponent implements AfterViewInit, OnDestroy{
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly injector = inject(Injector);

  readonly autoSize = input(true, { transform: booleanAttribute });
  readonly resizeDebounceTime = input(150);
  readonly resizeMaxWaitTime = input(300);

  onResize() {
    this.resizeToContainer();
  }

  private autosizingObserver: AutosizingObserver;

  ngAfterViewInit(): void {
    this.autosizingObserver = new AutosizingObserver(
      this.autoSize,
      () => this.onResize(),
      this.hostElement,
      this.injector,
      {
        destroyRef: this.destroyRef,
        resizeDebounceTime: this.resizeDebounceTime,
        resizeMaxWaitTime: this.resizeMaxWaitTime,
      }
    );

    this.autosizingObserver.init();

    this.recreateChart();
  }

  ngOnDestroy(): void {
    this.autosizingObserver?.destroy();
  };

  readonly zone = inject(NgZone);
  readonly destroyRef = inject(DestroyRef);
  readonly hostElement = inject(ElementRef<HTMLElement>);

  //#region Options
  readonly autoUpdate = input(true, { transform: booleanAttribute });

  readonly accessibility = input<Highcharts.AccessibilityOptions>();
  readonly annotations = input<Array<Highcharts.AnnotationsOptions>>();
  readonly caption = input<Highcharts.CaptionOptions>();
  readonly chartOptions = input<Highcharts.ChartOptions>();
  readonly colorAxis = input<Highcharts.ColorAxisOptions | Array<Highcharts.ColorAxisOptions>>();
  readonly colors = input<Highcharts.ColorString | Highcharts.GradientColorObject | Highcharts.PatternObject>();
  readonly credits = input<Highcharts.CreditsOptions>();
  readonly drilldown = input<Highcharts.DrilldownOptions>();
  readonly lang = input<Highcharts.LangOptions>();
  readonly legend = input<Highcharts.LegendOptions>();
  readonly loading = input<Highcharts.LoadingOptions>();
  readonly navigation = input<Highcharts.NavigationOptions>();
  readonly navigator = input<Highcharts.NavigatorOptions>();
  readonly pane = input<Highcharts.PaneOptions | Array<Highcharts.PaneOptions>>();
  readonly plotOptions = input<Highcharts.PlotOptions>();
  readonly rangeSelector = input<Highcharts.RangeSelectorOptions>();
  readonly responsive = input<Highcharts.ResponsiveOptions>();
  readonly scrollbar = input<Highcharts.ScrollbarOptions>();
  readonly series = input<Array<Highcharts.SeriesOptionsType>>();
  readonly subtitle = input<Highcharts.SubtitleOptions>();
  readonly time = input<Highcharts.TimeOptions>();
  readonly title = input<Highcharts.TitleOptions>();
  readonly tooltip = input<Highcharts.TooltipOptions>();
  readonly xAxis = input<Highcharts.XAxisOptions | Array<Highcharts.XAxisOptions>>();
  readonly yAxis = input<Highcharts.YAxisOptions | Array<Highcharts.YAxisOptions>>();
  readonly zAxis = input<Highcharts.YAxisOptions | Array<Highcharts.ZAxisOptions>>();
  
  private readonly __accessibilityChanged = effect(() => this.optionsInputChanged(this.accessibility(), 'accessibility'));
  private readonly __annotationsChanged = effect(() => this.optionsInputChanged(this.annotations(), 'annotations'));
  private readonly __captionChanged = effect(() => this.optionsInputChanged(this.caption(), 'caption'));
  private readonly __chartOptionsChanged = effect(() => this.optionsInputChanged(this.chartOptions(), 'chart'));
  private readonly __colorAxisChanged = effect(() => this.optionsInputChanged(this.colorAxis(), 'colorAxis'));
  private readonly __colorsChanged = effect(() => this.optionsInputChanged(this.colors(), 'colors'));
  private readonly __creditsChanged = effect(() => this.optionsInputChanged(this.credits(), 'credits'));
  private readonly __drilldownChanged = effect(() => this.optionsInputChanged(this.drilldown(), 'drilldown'));
  private readonly __langChanged = effect(() => this.optionsInputChanged(this.lang(), 'lang'));
  private readonly __legendChanged = effect(() => this.optionsInputChanged(this.legend(), 'legend'));
  private readonly __loadingChanged = effect(() => this.optionsInputChanged(this.loading(), 'loading'));
  private readonly __navigationChanged = effect(() => this.optionsInputChanged(this.navigation(), 'navigation'));
  private readonly __navigatorChanged = effect(() => this.optionsInputChanged(this.navigator(), 'navigator'));
  private readonly __paneChanged = effect(() => this.optionsInputChanged(this.pane(), 'pane'));
  private readonly __plotOptionsChanged = effect(() => this.optionsInputChanged(this.plotOptions(), 'plotOptions'));
  private readonly __rangeSelectorChanged = effect(() => this.optionsInputChanged(this.rangeSelector(), 'rangeSelector'));
  private readonly __responsiveChanged = effect(() => this.optionsInputChanged(this.responsive(), 'responsive'));
  private readonly __scrollbarChanged = effect(() => this.optionsInputChanged(this.scrollbar(), 'scrollbar'));
  private readonly __seriesChanged = effect(() => this.optionsInputChanged(this.series(), 'series'));
  private readonly __subtitleChanged = effect(() => this.optionsInputChanged(this.subtitle(), 'subtitle'));
  private readonly __timeChanged = effect(() => this.optionsInputChanged(this.time(), 'time'));
  private readonly __titleChanged = effect(() => this.optionsInputChanged(this.title(), 'title'));
  private readonly __tooltipChanged = effect(() => this.optionsInputChanged(this.tooltip(), 'tooltip'));
  private readonly __xAxisChanged = effect(() => this.optionsInputChanged(this.xAxis(), 'xAxis'));
  private readonly __yAxisChanged = effect(() => this.optionsInputChanged(this.yAxis(), 'yAxis'));
  private readonly __zAxisChanged = effect(() => this.optionsInputChanged(this.zAxis(), 'zAxis'));

  private optionsInputChanged(value: any, property: string) {
    if (untracked(() => this.chart()) && this.autoUpdate()) {
      let options = {};

      options[property] = value;
      this.applyChartOptions(options);
    }
  }


  readonly baseOptions = input<Highcharts.Options>({
    title: { text: null },
    colors: HighchartsChart.defaultColors,
    chart: { reflow: false },
  });

  private readonly __baseOptionsChanged = effect(() => {
    if (this.autoUpdate()) {
      let mergedOptions = this.getMergedOptions();
      if (untracked(() => this.chart())) {
        this.applyChartOptions(mergedOptions);
      }
    }
  });


  getMergedOptions() {
    let options: any = deepClone(this.baseOptions() ?? {});

    mergeProperty(options['accessibility'], this.accessibility(), 'accessibility');
    mergeProperty(options['annotations'], this.annotations(), 'annotations');
    mergeProperty(options['caption'], this.caption(), 'caption');
    mergeProperty(options['chart'], this.chartOptions(), 'chart');
    mergeProperty(options['colorAxis'], this.colorAxis(), 'colorAxis');
    mergeProperty(options['colors'], this.colors(), 'colors');
    mergeProperty(options['credits'], this.credits(), 'credits');
    mergeProperty(options['drilldown'], this.drilldown(), 'drilldown');
    mergeProperty(options['lang'], this.lang(), 'lang');
    mergeProperty(options['legend'], this.legend(), 'legend');
    mergeProperty(options['loading'], this.loading(), 'loading');
    mergeProperty(options['navigation'], this.navigation(), 'navigation');
    mergeProperty(options['navigator'], this.navigator(), 'navigator');
    mergeProperty(options['pane'], this.pane(), 'pane');
    mergeProperty(options['plotOptions'], this.plotOptions(), 'plotOptions');
    mergeProperty(options['rangeSelector'], this.rangeSelector(), 'rangeSelector');
    mergeProperty(options['responsive'], this.responsive(), 'responsive');
    mergeProperty(options['scrollbar'], this.scrollbar(), 'scrollbar');
    mergeProperty(options['series'], this.series(), 'series');
    mergeProperty(options['subtitle'], this.subtitle(), 'subtitle');
    mergeProperty(options['time'], this.time(), 'time');
    mergeProperty(options['title'], this.title(), 'title');
    mergeProperty(options['tooltip'], this.tooltip(), 'tooltip');
    mergeProperty(options['xAxis'], this.xAxis(), 'xAxis');
    mergeProperty(options['yAxis'], this.yAxis(), 'yAxis');
    mergeProperty(options['zAxis'], this.zAxis(), 'zAxis');

    return <Highcharts.Options>options;

    function mergeProperty(current: any, toMerge: any, property: string) {
      if (toMerge !== undefined) {
        options[property] = toMerge;
      }
    }
  }
  //#endregion

  //#region Chart
  readonly chart = signal<Highcharts.Chart>(null);

  readonly chartContainer = viewChild('chartContainer', { read: ElementRef<HTMLElement> });

  applyChartOptions(options: Highcharts.Options, redraw = true, oneToOne = true) {
    let chart = this.chart();
    if (!chart)
      return;

    this.zone.runOutsideAngular(() => chart.update(options, redraw, oneToOne));
  }

  redrawChart() {
    let chart = this.chart();
    if (!chart)
      return;

    this.zone.runOutsideAngular(() => chart.redraw());
  }

  // Store chart reference for cleanup (this may not be the same as the signal's value).
  private _chart: Highcharts.Chart;

  recreateChart() {
    this.destroyChart();

    let chartContainer = this.chartContainer().nativeElement;
    let options = this.getMergedOptions();

    this.zone.runOutsideAngular(() => {
      this._chart = Highcharts.chart(chartContainer, options, chart => {
        this.zone.run(() => {
          this.chart.set(chart);
        });
      });
    });
  }

  private destroyChart() {
    let chart = this._chart;
    if (!chart)
      return;

    this._chart = null;

    try {
      this.zone.runOutsideAngular(() => {
        chart.destroy();
      });
    }
    finally {
      this.chart.set(null);
    }
  }
  //#endregion

  //#region Size
  readonly size = model<Highcharts.SizeObject>(null);

  private readonly sizeChange = effect(() => {
    let size = this.size();
    let chart = this.chart();
    if (!size || !chart)
      return;
      
    this.setChartSizeDebounced(size);
  });

  private setChartSizeDebounced = debounced((size: any) => {
    this.zone.runOutsideAngular(() => untracked(() => this.chart().setSize(size.width, size.height, false)));
  }, 20 + Math.random() * 50, this.destroyRef);

  setSize(size: Highcharts.SizeObject): void;
  setSize(width: number, height: number): void;
  setSize(sizeOrWidth: Highcharts.SizeObject | number, height_?: number): void {
    if (this.autoSize()) {
      LOGGER.warn(`The chart is auto sizing. Explicitly set size will be overwritten.`, { component: this });
    }

    let size =
      typeof sizeOrWidth === 'object'
        ? sizeOrWidth
        : { width: sizeOrWidth, height: height_ };

    this.applySize(size);
  }

  private applySize(size: Highcharts.SizeObject) {
    size.width = Math.floor(size.width);
    size.height = Math.floor(size.height);

    let currentSize = this.size();
    if (currentSize && currentSize.width == size.width && currentSize.height == size.height) {
      return;
    }

    this.size.set(size);
  }


  readonly containerSize = signal<Size>({ w: 0, h: 0 }, {
    equal: (a, b) => a.w == b.w && a.h == b.h
  });

  readonly containerSizeChange = output<Size>();


  getContainerSize() {
    let _ = this.containerSize();
    return this.getContainerSizeDirect();
  }

  private getContainerSizeDirect() {
    let element = this.findVisibleContainer();
    let w = Math.floor(element.offsetWidth);
    let h = Math.floor(element.offsetHeight);
    return { w, h };
  }

  private findVisibleContainer() {
    let e = (this.chartContainer()?.nativeElement ?? this.hostElement.nativeElement) as HTMLElement;
    while (!e.offsetWidth) {
      e = e.parentElement;
    }

    return e;
  }

  resizeToContainer() {
    let size = this.getContainerSizeDirect();
    this.containerSize.set(size);
    this.containerSizeChange.emit(size);

    this.applySize({ width: size.w, height: size.h });
  }
}

export interface HighchartsEvent<T> {
  readonly component: PbirHighchartsChartComponent;
  readonly chart: Highcharts.Chart;
  readonly event: T;
}

export namespace HighchartsChart {
  export type OptionsBuilder = (options: Highcharts.Options) => void;

  export enum LegendLayout { Horizontal = 'horizontal', Vertical = 'vertical' };
  export enum LegendAlign { Left = 'left', Center = 'center', Right = 'right' };
  export enum LegendVerticalAlign { Top = 'top', Middle = 'middle', Bottom = 'bottom' };
  export enum LegendSymbol { None, Square, Rectangle, Circle };
  export type LegendLabelFormatter = (pointOrSeries: Highcharts.Point | Highcharts.Series) => string;
  
  export type TooltipFormatter = (point: Highcharts.Point, tooltip: Highcharts.Tooltip) => string | string[] | false;
  export type TooltipPointFormatter = (point: Highcharts.Point) => string;

  export type ClickEvent = HighchartsEvent<Highcharts.PointerEventObject>;
  export type LoadEvent = HighchartsEvent<Event>;
  export type RedrawEvent = HighchartsEvent<Event>;
  export type RenderEvent = HighchartsEvent<Event>;
  export type SelectionEvent = HighchartsEvent<Highcharts.SelectEventObject>;

  export type DrillDownEvent = HighchartsEvent<Highcharts.DrilldownEventObject>;
  export type DrillUpEvent = HighchartsEvent<Highcharts.DrillupEventObject>;
  export type DrillUpAllEvent = HighchartsEvent<Highcharts.DrillupAllEventObject>;

  export namespace Classes {
    export const
      Chart   = 'pbir-highcharts-chart',
      Legend  = 'pbir-highcharts-legend',
      Tooltip = 'pbir-highcharts-tooltip';
  }

  export const defaultColors = [
    '#004c77',
    '#0087cb',
    '#f29100',
    '#979797',
    '#80C3E5',
    '#e0e0e0',
    '#00395A',

    '#386d9b',
    '#006cad',
    '#c66d00',
    '#777777',
    '#559abb',
    '#afafaf',
    '#345d81',

    '#345d81',
    '#5f90c1',
    '#005291',
    '#9c4b00',
    '#585858',
    '#277392',
    '#808080',
    '#5d83a9',

    '#86b5e8',
    '#003a75',
    '#752a00',
    '#3b3b3b',
    '#004e6b',
    '#545454',
    '#86acd4',

    '#addcff',
    '#00245a',
    '#530500',
    '#202021',
    '#002c47',
    '#2c2c2c',
    '#b1d7ff',
  ];

  export function getColorByIndex(index: number) {
    return HighchartsChart.defaultColors[index % HighchartsChart.defaultColors.length];
  }

  export const defaultSymbols = [
    'circle', 'square','diamond', 'triangle', 'triangle-down'
  ];

  export function getSymbolByIndex(index: number) {
    return HighchartsChart.defaultSymbols[index % HighchartsChart.defaultSymbols.length];
  }
}
